import type { ZodTypeAny, z } from 'zod';
import {
  ListBatchesRequestSchema, ListBatchesResponseSchema,
  GetBatchRequestSchema, GetBatchResponseSchema,
  CreateBatchRequestSchema, CreateBatchResponseSchema,
  RetryBatchRequestSchema, RetryBatchResponseSchema,
  CancelBatchRequestSchema, CancelBatchResponseSchema,
  BatchOverviewResponseSchema,
} from './schema/batch.js';
import {
  ListAssetsRequestSchema, ListAssetsResponseSchema,
  RequestUploadUrlRequestSchema, RequestUploadUrlResponseSchema,
  ConfirmUploadRequestSchema, ConfirmUploadResponseSchema,
} from './schema/asset.js';
import { OkSchema } from './schema/common.js';
import { AppConfigSchema } from './schema/config.js';
import {
  GetMeResponseSchema,
  RedeemInviteRequestSchema, RedeemInviteResponseSchema,
} from './schema/account.js';

/** REST 路由定义：path + method + 入参 schema + 出参 schema */
export type Route<Req extends ZodTypeAny | null, Res extends ZodTypeAny> = {
  path: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  /** GET 用 query，POST/PUT/DELETE 用 body */
  request: Req;
  response: Res;
};

const route = <Req extends ZodTypeAny | null, Res extends ZodTypeAny>(
  path: string,
  method: Route<Req, Res>['method'],
  request: Req,
  response: Res,
): Route<Req, Res> => ({ path, method, request, response });

/** 全部 P0 路由 */
export const routes = {
  // batch
  listBatches:    route('/api/batch/list',     'GET',  ListBatchesRequestSchema,  ListBatchesResponseSchema),
  getBatch:       route('/api/batch/detail',   'GET',  GetBatchRequestSchema,     GetBatchResponseSchema),
  createBatch:    route('/api/batch/create',   'POST', CreateBatchRequestSchema,  CreateBatchResponseSchema),
  retryBatch:     route('/api/batch/retry',    'POST', RetryBatchRequestSchema,   RetryBatchResponseSchema),
  cancelBatch:    route('/api/batch/cancel',   'POST', CancelBatchRequestSchema,  CancelBatchResponseSchema),
  batchOverview:  route('/api/batch/overview', 'GET',  null,                      BatchOverviewResponseSchema),

  // asset
  listAssets:     route('/api/asset/list',           'GET',  ListAssetsRequestSchema,      ListAssetsResponseSchema),
  requestUpload:  route('/api/asset/upload-url',     'POST', RequestUploadUrlRequestSchema, RequestUploadUrlResponseSchema),
  confirmUpload:  route('/api/asset/upload-confirm', 'POST', ConfirmUploadRequestSchema,    ConfirmUploadResponseSchema),

  // config（前端配置同步到 BFF；私有化部署/凭证服务端代管时用）
  getConfig:      route('/api/config/get',  'GET',  null,            AppConfigSchema),
  putConfig:      route('/api/config/put',  'POST', AppConfigSchema, OkSchema),

  // account / 邀请码 / 额度（Slice 9）
  getMe:          route('/api/me',             'GET',  null,                     GetMeResponseSchema),
  redeemInvite:   route('/api/invite/redeem',  'POST', RedeemInviteRequestSchema, RedeemInviteResponseSchema),

  // health
  health:         route('/health',          'GET',  null,            OkSchema),
} as const;

export type Routes = typeof routes;
export type RouteName = keyof Routes;

/** 推导 route 的 req/res 类型工具 */
export type RouteReq<R extends Route<any, any>> = R['request'] extends ZodTypeAny ? z.infer<R['request']> : void;
export type RouteRes<R extends Route<any, any>> = z.infer<R['response']>;
